/**
 * "fx" popover listing functions the formula engine supports. Each entry has a
 * click-to-insert template. Templates use a ``$0`` marker for where the caret
 * (or the user's current text selection) should land — so picking a function
 * while text is selected *wraps* that text as the function's argument, which is
 * how nested expressions like ``round(total_sales * 0.05, 2)`` get composed.
 *
 * Dialect: the Formula (E) field evaluates with the full sandboxed pandas/numpy
 * engine (core/formula_eval.py) and shows everything. The If/Then/Else fields
 * still run on the narrower ``df.eval`` engine, so they only show the subset
 * that engine actually supports (no if()/where()/np/dict/lambda/string-concat).
 */
import { useState } from 'react';
import {
  Box, Chip, Divider, IconButton, List, ListItemButton, ListItemText,
  Popover, Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import FunctionsIcon from '@mui/icons-material/Functions';

export type FormulaDialect = 'formula' | 'frame';

interface Props {
  /** Insert the chosen template ($0 = caret / selection slot). */
  onInsert: (snippet: string) => void;
  /** 'formula' = full pandas engine (E-type); 'frame' = df.eval subset. */
  dialect?: FormulaDialect;
}

interface FnEntry {
  /** What the user sees in the list. */
  label: string;
  /** Template to insert. ``$0`` marks the caret / selection-wrap position. */
  snippet: string;
  /** One-line description shown under the label. */
  hint: string;
}

// Categories whose entries work in BOTH df.eval and the full formula engine.
const BASE: Record<string, FnEntry[]> = {
  Math: [
    { label: 'abs(x)',    snippet: 'abs($0)',    hint: 'Absolute value' },
    { label: 'sqrt(x)',   snippet: 'sqrt($0)',   hint: 'Square root' },
    { label: 'floor(x)',  snippet: 'floor($0)',  hint: 'Round down to integer' },
    { label: 'ceil(x)',   snippet: 'ceil($0)',   hint: 'Round up to integer' },
    { label: 'log(x)',    snippet: 'log($0)',    hint: 'Natural log' },
    { label: 'log10(x)',  snippet: 'log10($0)',  hint: 'Log base 10' },
    { label: 'exp(x)',    snippet: 'exp($0)',    hint: 'e to the x' },
    { label: 'x ** 2',    snippet: '$0 ** 2',    hint: 'Power (** is exponent)' },
  ],
  Helpers: [
    { label: 'udf_round(x, n)',  snippet: 'udf_round($0, 2)',  hint: 'Round to n decimal places' },
    { label: 'udf_divide(x, y)', snippet: 'udf_divide($0, y)', hint: 'Safe division (x / y)' },
    { label: 'contribution(x)',  snippet: 'contribution($0)',  hint: '% share of total (x / sum * 100)' },
  ],
  Comparison: [
    { label: 'a > b',   snippet: '$0 > b',   hint: 'Greater than' },
    { label: 'a >= b',  snippet: '$0 >= b',  hint: 'Greater than or equal' },
    { label: 'a < b',   snippet: '$0 < b',   hint: 'Less than' },
    { label: 'a == b',  snippet: '$0 == b',  hint: 'Equal (note: two = signs)' },
    { label: 'a != b',  snippet: '$0 != b',  hint: 'Not equal' },
    { label: 'a & b',   snippet: '$0 & b',   hint: 'Logical AND' },
    { label: 'a | b',   snippet: '$0 | b',   hint: 'Logical OR' },
    { label: '~a',      snippet: '~$0',      hint: 'Logical NOT' },
  ],
  Text: [
    { label: 'col.str.upper()',     snippet: '$0.str.upper()',          hint: 'Uppercase a text column' },
    { label: 'col.str.lower()',     snippet: '$0.str.lower()',          hint: 'Lowercase a text column' },
    { label: 'col.str.len()',       snippet: '$0.str.len()',            hint: 'String length per row' },
    { label: 'col.str.strip()',     snippet: '$0.str.strip()',          hint: 'Trim whitespace' },
    { label: 'col.str.contains(s)', snippet: "$0.str.contains('text')", hint: 'True if substring is present' },
    { label: 'col.str.startswith', snippet: "$0.str.startswith('PRE')", hint: 'True if starts with' },
  ],
  'Missing values': [
    { label: 'col.fillna(0)',    snippet: '$0.fillna(0)',    hint: 'Replace NaN/null with a value' },
    { label: 'col.isna()',       snippet: '$0.isna()',       hint: 'True where the cell is missing' },
    { label: 'col.notna()',      snippet: '$0.notna()',      hint: 'True where the cell has a value' },
    { label: 'col.clip(0, 100)', snippet: '$0.clip(0, 100)', hint: 'Bound values to a range' },
  ],
  'Window & group': [
    { label: 'col.cumsum()',  snippet: '$0.cumsum()',  hint: 'Running total down the column' },
    { label: 'col.rank()',    snippet: '$0.rank()',    hint: 'Rank of each value' },
    { label: 'col.shift(1)',  snippet: '$0.shift(1)',  hint: 'Previous row’s value' },
    { label: 'col.rolling(n).mean()', snippet: '$0.rolling(2).mean()', hint: 'Moving average over n rows' },
    { label: 'group total', snippet: "$0.groupby(region).transform('sum')", hint: 'Total within each group' },
  ],
};

// Categories that need the full formula engine (unsupported by df.eval).
const FORMULA_ONLY: Record<string, FnEntry[]> = {
  Logic: [
    { label: 'if(cond, a, b)',    snippet: 'if($0, a, b)',     hint: 'If cond then a else b, row by row' },
    { label: 'round(x, n)',       snippet: 'round($0, 2)',     hint: 'Round to n decimals (any value)' },
    { label: 'where(cond, a, b)', snippet: 'where($0, a, b)',  hint: 'Same as if() — vectorised choose' },
    { label: 'maximum(a, b)',     snippet: 'maximum($0, b)',   hint: 'Larger of two values, per row' },
    { label: 'minimum(a, b)',     snippet: 'minimum($0, b)',   hint: 'Smaller of two values, per row' },
    { label: 'col.map({...})',    snippet: "$0.map({'x': 1, 'y': 2})", hint: 'Map each value via a lookup table' },
    { label: 'col.apply(fn)',     snippet: '$0.apply(lambda v: v + 1)', hint: 'Run a small function per value' },
    { label: 'a + "_" + b',       snippet: "$0 + '_' + b",     hint: 'Join text columns together' },
  ],
};

export default function FunctionPicker({ onInsert, dialect = 'frame' }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // Formula dialect leads with Logic (the most-asked-for functions); the
  // narrower frame dialect omits it entirely.
  const categories: Record<string, FnEntry[]> =
    dialect === 'formula' ? { ...FORMULA_ONLY, ...BASE } : { ...BASE };
  const cats = Object.keys(categories);
  const [cat, setCat] = useState<string>(cats[0]);
  // Guard against a stale tab if the dialect (and thus cats) changed.
  const activeCat = categories[cat] ? cat : cats[0];

  const open = Boolean(anchor);

  return (
    <>
      <Tooltip title="Insert a function">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}
        >
          <FunctionsIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 440, maxHeight: 440 } } }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Typography variant="subtitle2">Supported functions</Typography>
            <Chip size="small" variant="outlined"
              label="Select text first to wrap it" />
          </Stack>
        </Box>
        <Divider />
        <Tabs
          value={activeCat}
          onChange={(_, v) => setCat(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
        >
          {cats.map((c) => (
            <Tab key={c} label={c} value={c} sx={{ minHeight: 36, py: 0.5 }} />
          ))}
        </Tabs>
        <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {categories[activeCat].map((fn) => (
            <ListItemButton
              key={fn.label}
              onClick={() => { onInsert(fn.snippet); setAnchor(null); }}
            >
              <ListItemText
                primary={<code style={{ fontFamily: 'monospace', fontSize: 14 }}>{fn.label}</code>}
                secondary={fn.hint}
              />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <Box sx={{ p: 1, bgcolor: 'grey.50' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Tip: nest freely — e.g. <code>round(if(a &gt; b, a, b), 2)</code>.
            Select part of your formula, then click a function to wrap it.
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
